import React, { useState, useEffect, useCallback } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Platform,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useLocalSearchParams, router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import Header from "@/components/ui/Header";
import { useBills } from "@/hooks/useBills";
import BillServices from "@/services/billServices";
import { showError, showSuccess } from "@/utils/toast";
import { RefundProcess } from "@/types/refundProcess";
import { BillWithRefundProcess, UserBill } from "@/types/bill";
import { PhoneRefundFlowModal } from "@/components/hotelReservation/PhoneRefundFlowModal";
import DeleteConfirmationModal from "@/components/common/DeleteConfirmationModal";
import { OfficeHoursModal } from "@/components/OfficeHoursModal";
import { isWithinBusinessHours } from "@/utils/business-hours";
import dayjs from "dayjs";

const BillDetailScreen = () => {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { bills, refetch, deleteBill } = useBills();
  const insets = useSafeAreaInsets();
  const [isDeleting, setIsDeleting] = useState(false);
  const [refundProcesses, setRefundProcesses] = useState<RefundProcess[]>([]);
  const [isLoadingRefunds, setIsLoadingRefunds] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOfficeHoursModal, setShowOfficeHoursModal] = useState(false);

  const bill = bills.find(
    (b) =>
      `${b.accountNumber}#${b.billingDate}` === decodeURIComponent(id || "")
  );

  const loadRefundProcesses = useCallback(async () => {
    if (!bill?.accountNumber || !bill?.billingDate) return;

    setIsLoadingRefunds(true);
    try {
      const processes = await BillServices.getBillRefundProcesses(
        bill.accountNumber,
        bill.billingDate
      );
      setRefundProcesses(processes);
    } catch (error) {
      console.error("Failed to load refund processes:", error);
    } finally {
      setIsLoadingRefunds(false);
    }
  }, [bill?.accountNumber, bill?.billingDate]);

  useEffect(() => {
    if (bill?.accountNumber && bill?.billingDate) {
      loadRefundProcesses();
    }
  }, [bill?.accountNumber, bill?.billingDate, loadRefundProcesses]);

  const handleDelete = () => {
    if (Platform.OS === "web") {
      setShowDeleteModal(true);
    } else {
      Alert.alert(
        "Delete Bill",
        "Are you sure you want to delete this bill? This action cannot be undone.",
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Delete",
            style: "destructive",
            onPress: confirmDelete,
          },
        ]
      );
    }
  };

  const confirmDelete = async () => {
    if (!bill?.accountNumber || !bill?.billingDate) return;

    setIsDeleting(true);
    try {
      const success = await deleteBill(bill.accountNumber, bill.billingDate);
      if (success) {
        showSuccess("Bill deleted successfully");
        router.back();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || "Failed to delete bill");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const convertBillToRefundProcessItem = (
    bill: UserBill | undefined
  ): BillWithRefundProcess | null => {
    if (!bill) return null;

    return {
      ...bill,
      currentStatus: null,
      reasonForIntervention: null,
      refundProcessPk: undefined,
      refundProcessSk: undefined,
    };
  };

  const handleInitiateRefund = () => {
    if (!bill?.accountNumber || !bill?.billingDate) {
      showError("Account number or billing date not found for this bill");
      return;
    }

    // Check if within business hours
    const withinHours = isWithinBusinessHours();
    console.log('Business hours check:', {
      withinHours,
      currentTime: new Date().toLocaleTimeString(),
      currentDay: new Date().getDay(),
    });

    if (!withinHours) {
      setShowOfficeHoursModal(true);
      return;
    }

    // Proceed with refund if within business hours
    setShowRefundModal(true);
  };

  const handleCallAnyway = () => {
    setShowOfficeHoursModal(false);
    setShowRefundModal(true);
  };

  const handleCancelOfficeHours = () => {
    setShowOfficeHoursModal(false);
  };

  const getRefundStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "#10B981";
      case "rejected":
        return "#EF4444";
      case "waiting_merchant_response":
        return "#F59E0B";
      case "need_user_intervention":
        return "#8B5CF6";
      default:
        return "#6B7280";
    }
  };

  const formatRefundStatus = (status: string) => {
    switch (status) {
      case "waiting_merchant_response":
        return "Waiting Merchant Response";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "need_user_intervention":
        return "Needs User Action";
      default:
        return status
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  if (!bill) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Bill Details" />
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>Bill not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Bill Details" />

      <ScrollView
        style={styles.content}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: Math.max(insets.bottom, 34) + 20 },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Bill Info Section */}
        <View style={styles.section}>
          <View style={styles.billHeader}>
            <View style={styles.billInfo}>
              <Text style={styles.billProvider}>{bill.provider}</Text>
              <Text style={styles.billType}>
                {bill.billType.charAt(0).toUpperCase() + bill.billType.slice(1)}{" "}
                Bill
              </Text>
            </View>
            <Text style={styles.billAmount}>
              {bill.currency}{" "}
              {bill.totalAmount ? bill.totalAmount.toFixed(2) : "0.00"}
            </Text>
          </View>
        </View>

        {/* Details Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Bill Information</Text>

          {bill.invoiceNumber && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Invoice Number:</Text>
              <Text style={styles.detailValue}>{bill.invoiceNumber}</Text>
            </View>
          )}

          {bill.accountNumber && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Account Number:</Text>
              <Text style={styles.detailValue}>{bill.accountNumber}</Text>
            </View>
          )}

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Billing Date:</Text>
            <Text style={styles.detailValue}>
              {dayjs(bill.billingDate).format("MMM DD, YYYY")}
            </Text>
          </View>

          {bill.customerName && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Customer Name:</Text>
              <Text style={styles.detailValue}>{bill.customerName}</Text>
            </View>
          )}

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Due Date:</Text>
            <Text style={styles.detailValue}>
              {dayjs(bill.dueDate).format("MMM DD, YYYY")}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Billing Period:</Text>
            <Text style={styles.detailValue}>
              {dayjs(bill.billingPeriod.startDate).format("MMM DD")} -{" "}
              {dayjs(bill.billingPeriod.endDate).format("MMM DD, YYYY")}
            </Text>
          </View>
        </View>

        {/* Services Section */}
        {bill.services && bill.services.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Services</Text>
            {bill.services.map((service, index) => (
              <View key={index} style={styles.serviceRow}>
                <View style={styles.serviceInfo}>
                  <Text style={styles.serviceName}>
                    {service.name || "Unknown Service"}
                  </Text>
                  {service.description && (
                    <Text style={styles.serviceDescription}>
                      {service.description}
                    </Text>
                  )}
                </View>
                <Text style={styles.serviceAmount}>
                  {bill.currency}{" "}
                  {service.amount ? service.amount.toFixed(2) : "0.00"}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Refund Processes Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Refund Processes</Text>
            {isLoadingRefunds && (
              <ActivityIndicator size="small" color="#6B7280" />
            )}
          </View>

          {!isLoadingRefunds &&
          (!refundProcesses || refundProcesses.length === 0) ? (
            <Text style={styles.emptyText}>No refund processes found</Text>
          ) : (
            refundProcesses &&
            Array.isArray(refundProcesses) &&
            refundProcesses.map((process, index) => (
              <View
                key={index}
                style={[
                  styles.refundProcess,
                  index > 0 && styles.refundProcessBorder,
                ]}
              >
                <View style={styles.refundHeader}>
                  <Text style={styles.refundId}>
                    #
                    {process.pk ? process.pk.slice(-8) : `Process ${index + 1}`}
                  </Text>
                  <View
                    style={[
                      styles.statusBadge,
                      {
                        backgroundColor:
                          getRefundStatusColor(process.currentStatus) + "20",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.statusText,
                        { color: getRefundStatusColor(process.currentStatus) },
                      ]}
                    >
                      {formatRefundStatus(process.currentStatus)}
                    </Text>
                  </View>
                </View>

                <View style={styles.refundDetails}>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Created:</Text>
                    <Text style={styles.detailValue}>
                      {process.createdAt
                        ? dayjs(process.createdAt).format("MMM DD, YYYY HH:mm")
                        : "N/A"}
                    </Text>
                  </View>

                  {process.approvedRefundAmount &&
                    typeof process.approvedRefundAmount === "number" && (
                      <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Refund Amount:</Text>
                        <Text style={styles.detailValue}>
                          {bill.currency}{" "}
                          {process.approvedRefundAmount.toFixed(2)}
                        </Text>
                      </View>
                    )}

                  {process.reason && (
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>Reason:</Text>
                      <Text style={styles.detailValue}>{process.reason}</Text>
                    </View>
                  )}
                </View>
              </View>
            ))
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.actionSection}>
          <TouchableOpacity
            style={[styles.actionButton, styles.refundButton]}
            onPress={handleInitiateRefund}
          >
            <Ionicons name="refresh-outline" size={24} color="#FFF" />
            <Text style={styles.refundButtonText}>Initiate Refund</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.deleteButton]}
            onPress={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <ActivityIndicator size="small" color="#FFF" />
            ) : (
              <Ionicons name="trash-outline" size={24} color="#FFF" />
            )}
            <Text style={styles.deleteButtonText}>
              {isDeleting ? "Deleting..." : "Delete Bill"}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Refund Modal */}
      {convertBillToRefundProcessItem(bill) && (
        <PhoneRefundFlowModal
          visible={showRefundModal}
          item={convertBillToRefundProcessItem(bill)!}
          onClose={() => {
            setShowRefundModal(false);
            refetch();
            loadRefundProcesses();
          }}
        />
      )}

      {/* Delete Confirmation Modal for Web */}
      <DeleteConfirmationModal
        visible={showDeleteModal}
        title="Delete Bill"
        message="Are you sure you want to delete this bill? This action cannot be undone."
        isDeleting={isDeleting}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
      />

      {/* Office Hours Warning Modal */}
      <OfficeHoursModal
        visible={showOfficeHoursModal}
        onCallAnyway={handleCallAnyway}
        onCancel={handleCancelOfficeHours}
      />
    </SafeAreaView>
  );
};

export default BillDetailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    fontSize: 16,
    color: "#6B7280",
    fontFamily: FontFamilies.Inter.Regular,
  },
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
    marginHorizontal: 16,
  },
  billHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  billInfo: {
    flex: 1,
  },
  billProvider: {
    fontSize: 20,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#1F2937",
  },
  billType: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    marginTop: 2,
  },
  billAmount: {
    fontSize: 24,
    fontFamily: FontFamilies.InterTight.Bold,
    color: "#0F9129",
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#1F2937",
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  detailLabel: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    flex: 1,
  },
  detailValue: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#374151",
    flex: 1,
    textAlign: "right",
  },
  serviceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: "#E5E7EB",
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#374151",
  },
  serviceDescription: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    marginTop: 2,
  },
  serviceAmount: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#374151",
  },
  actionSection: {
    marginTop: 16,
    marginHorizontal: 16,
    gap: 12,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 8,
  },
  refundButton: {
    backgroundColor: "#0F9129",
  },
  refundButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
  },
  deleteButton: {
    backgroundColor: "#DC2626",
  },
  deleteButtonText: {
    color: "#FFF",
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: FontFamilies.Inter.Regular,
    textAlign: "center",
    paddingVertical: 16,
  },
  refundProcess: {
    paddingVertical: 12,
  },
  refundProcessBorder: {
    borderTopWidth: 0.5,
    borderTopColor: "#E5E7EB",
    marginTop: 12,
  },
  refundHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  refundId: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#374151",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.SemiBold,
  },
  refundDetails: {
    gap: 4,
  },
});
